import React, { useState } from 'react';
import { RootState } from '@/store';
import { useAppSelector } from '@/store/hooks';
import { refreshActiveTable } from '@/store/reducers/table-slice';
import { getUi, hideModal } from '@/store/reducers/ui-slice';
import { AdminRoles } from '@alien-platform/bo-sdk-v2';
import { Button, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'sonner';
import { z } from 'zod';

import { CustomConfigInputProps, InputType } from '@/types/general';
import { buildZodSchemaFromFields } from '@/lib/helpers';
import { CustomConfigInputV2 } from '@/components/core/custom-config-input-v2';

function ChangeRolesModalContent(): React.JSX.Element {
  const { modal } = useAppSelector(getUi);
  const { user } = useSelector((state: RootState) => state.user);

  const isSuperAdmin = user?.role === AdminRoles.SUPER_ADMIN || user?.role === AdminRoles.ADMIN;

  const rolesArray = isSuperAdmin
    ? Object.values(AdminRoles).map((role) => ({
        label: role.replace(/_/g, ' '),
        value: role,
      }))
    : Object.values(AdminRoles)
        .filter((item) => item !== AdminRoles.ADMIN && item !== AdminRoles.SUPER_ADMIN && item !== AdminRoles.DEV_OPS)
        .map((role) => ({
          label: role.replace(/_/g, ' '),
          value: role,
        }));

  const [dto, setDto] = useState({
    roles: '',
  });

  const content = modal?.content as {
    opId: string;
    userId: string;
    nickname: string;
  };

  const generalInputs: CustomConfigInputProps[] = [
    {
      label: 'New Role',
      name: 'roles',
      value: dto.roles,
      type: InputType.SELECT,
      menuItems: rolesArray,
      zod: z.string().min(1, 'New role is required'),
    },
  ];

  const newRoleSchema = buildZodSchemaFromFields(generalInputs);
  type NewRole = z.infer<typeof newRoleSchema>;
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof NewRole, string>>>({});

  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const handleClose = (): void => {
    dispatch(hideModal());
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    setFormErrors({}); //to clear previous errors if any

    const parseResult = newRoleSchema.safeParse(dto);

    if (!parseResult.success) {
      const errors: Partial<Record<keyof NewRole, string>> = {};

      const fieldErrors = parseResult.error.flatten().fieldErrors;
      for (const key in fieldErrors) {
        if (fieldErrors[key] && fieldErrors[key].length > 0) {
          errors[key] = fieldErrors[key][0];
        }
      }
      setFormErrors(errors);
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`/api/users/change-roles`, {
        method: 'PUT',
        body: JSON.stringify({ opId: content?.opId, userId: content?.userId, ...dto }),
      });
      const result = await response.json();
      if (response.ok && result.success && result.data) {
        toast.success('Role changed successfully!');
        dispatch(hideModal());
        dispatch(refreshActiveTable());
      } else {
        toast.error('Error: 500 | Unable to change role');
      }
    } catch {
      toast.error('Error: 500 | Unable to change role');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <Typography variant="body2">
          Select a new role for the user: <b>{content?.nickname}</b>
        </Typography>
        {generalInputs.map((input, index) => (
          <CustomConfigInputV2
            key={index}
            defaultData={dto}
            menuItems={rolesArray}
            setDefaultData={setDto}
            {...input}
            error={formErrors[input.name]}
            onChange={(__) => {
              setFormErrors((prev) => {
                const { [input.name]: _removed, ...rest } = prev;
                return rest;
              });
            }}
          />
        ))}

        <Stack direction="row" spacing={3} sx={{ mt: 3, justifyContent: 'initial' }}>
          <Button type="button" color="secondary" onClick={handleClose} sx={{ flex: '1 1 auto' }}>
            Close
          </Button>

          <Button loading={isLoading} type="submit" color="error" sx={{ flex: '1 1 auto' }} variant="contained">
            Change Role
          </Button>
        </Stack>
      </Stack>
    </form>
  );
}

export default ChangeRolesModalContent;
